/* Natural-language → cron, for the agent builder's schedule field.
 *
 * Non-technical users describe when an agent should run in plain
 * English ("every weekday at 9am"); this turns that into the 5-field
 * UTC cron string the backend stores and the worker tick matches on
 * (see functions/_lib/cron.ts). describeCron() does the reverse —
 * turning a cron string back into a human sentence — so the form can
 * confirm what it understood, and the agent list can read nicely.
 *
 * Deliberately a small, deterministic grammar (no LLM call): instant,
 * free, offline. It covers the common cadences and falls back to a
 * clear "couldn't read that" rather than guessing wildly. A power user
 * can still paste a raw 5-field cron and it passes straight through.
 */

const DOW = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};
const DOW_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pad2 = n => String(n).padStart(2, '0');

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function joinList(arr) {
  if (arr.length <= 1) return arr[0] || '';
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;
}

/** True when the string is already a valid-ish 5-field cron expression
 *  (same shape the backend accepts). Lets a power user paste raw cron. */
function looksLikeCron(s) {
  const parts = s.trim().split(/\s+/);
  return parts.length === 5 && parts.every(p => /^[\d*,\-/]+$/.test(p));
}

/** Day-of-week field from free text, or '*' for "any day". */
function parseDow(text) {
  if (/\bweekday(s)?\b/.test(text) || /\bmon(day)?\s*(?:-|to|through|–|—|until)\s*fri(day)?\b/.test(text)) return '1-5';
  if (/\bweekend(s)?\b/.test(text)) return '0,6';
  const days = new Set();
  for (const [name, n] of Object.entries(DOW)) {
    if (new RegExp(`\\b${name}s?\\b`).test(text)) days.add(n);
  }
  if (days.size) return [...days].sort((a, b) => a - b).join(',');
  return '*';
}

/** Day-of-month field from free text, or '*' for "every day". */
function parseDom(text) {
  const ord = text.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/);
  if (ord) {
    const d = Number(ord[1]);
    if (d >= 1 && d <= 31) return String(d);
  }
  if (/\bfirst of (the )?month\b/.test(text)) return '1';
  if (/\bmonthly\b/.test(text)) return '1';
  return '*';
}

/** {hour, minute} from "at 7am" / "07:00" / "noon" / "midnight", else null. */
function parseTime(text) {
  if (/\bnoon\b|\bmidday\b/.test(text)) return { hour: 12, minute: 0 };
  if (/\bmidnight\b/.test(text)) return { hour: 0, minute: 0 };
  const re = /(?:\bat\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/g;
  let m;
  while ((m = re.exec(text))) {
    const hasColon = m[2] != null;
    const ampm = m[3];
    const hasAt = /^at\s/.test(m[0]);
    // Only treat a bare number as a clock time when there's a clear cue
    // (am/pm, a colon, or the word "at"); otherwise it's a day-of-month
    // or interval count handled elsewhere.
    if (!hasColon && !ampm && !hasAt) continue;
    let hour = Number(m[1]);
    const minute = m[2] != null ? Number(m[2]) : 0;
    if (ampm === 'pm' && hour < 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
    if (hour > 23 || minute > 59) continue;
    return { hour, minute };
  }
  return null;
}

function finalize(cron) {
  try {
    return { cron, summary: describeCron(cron) };
  } catch {
    return { cron: '', error: 'Could not build a valid schedule from that.' };
  }
}

/**
 * Convert a plain-English schedule into a 5-field UTC cron expression.
 * Returns one of:
 *   { cron: '', summary: '' }         — blank input (manual run-only)
 *   { cron: '0 9 * * 1-5', summary }  — understood
 *   { cron: '', error }               — couldn't parse
 */
export function cronFromNatural(input) {
  const text = String(input || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!text) return { cron: '', summary: '' };

  if (looksLikeCron(text)) return finalize(text);

  const dow = parseDow(text);
  const dom = parseDom(text);

  // "every N minutes" / "every minute"
  let m = text.match(/every\s+(\d+)\s*(?:m|min|mins|minute|minutes)\b/);
  if (m || /every\s+minute\b/.test(text)) {
    const step = m ? Number(m[1]) : 1;
    if (!step || step < 1 || step > 59) return { cron: '', error: 'Minutes must be between 1 and 59.' };
    return finalize(`${step === 1 ? '*' : `*/${step}`} * ${dom} * ${dow}`);
  }

  // "every N hours" / "hourly" / "every hour"
  m = text.match(/every\s+(\d+)\s*(?:h|hr|hrs|hour|hours)\b/);
  if (m || /\bhourly\b/.test(text) || /every\s+hour\b/.test(text)) {
    const step = m ? Number(m[1]) : 1;
    if (!step || step < 1 || step > 23) return { cron: '', error: 'Hours must be between 1 and 23.' };
    return finalize(`0 ${step === 1 ? '*' : `*/${step}`} ${dom} * ${dow}`);
  }

  // Time-of-day: daily / weekly / monthly at a clock time.
  const time = parseTime(text);
  if (!time) {
    // A day or date with no clock time — nudge for one rather than guess.
    if (dow !== '*' || dom !== '*') return { cron: '', error: 'Add a time, e.g. “every Monday at 9am”.' };
    return { cron: '', error: 'Could not read that as a schedule.' };
  }
  return finalize(`${time.minute} ${time.hour} ${dom} * ${dow}`);
}

function describeDow(dow) {
  if (dow === '*') return '';
  if (dow === '1-5') return ' on weekdays (Mon–Fri)';
  if (dow === '0,6' || dow === '6,0') return ' on weekends (Sat & Sun)';
  if (dow.split(',').every(p => /^\d$/.test(p))) {
    const names = [...new Set(dow.split(',').map(Number))]
      .filter(n => n >= 0 && n <= 6)
      .sort((a, b) => a - b)
      .map(n => DOW_LABEL[n]);
    if (names.length) return ` on ${joinList(names)}`;
  }
  return ` (day-of-week ${dow})`;
}

function describeDom(dom) {
  if (dom === '*') return '';
  if (/^\d{1,2}$/.test(dom)) return ` on the ${ordinal(Number(dom))}`;
  return ` (day-of-month ${dom})`;
}

// minute, hour, day-of-month, month, day-of-week — matches the worker's
// matcher (functions/_lib/cron.ts).
const FIELD_RANGES = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 6]];

// True when every atom of a cron field sits inside [lo, hi]. Handles
// `*`, `N`, `N-N`, lists, and `/step` — same shapes the matcher reads.
function fieldInRange(raw, lo, hi) {
  for (const atom of raw.split(',')) {
    const slash = atom.indexOf('/');
    const base = slash === -1 ? atom : atom.slice(0, slash);
    if (slash !== -1) {
      const step = Number(atom.slice(slash + 1));
      if (!Number.isInteger(step) || step < 1) return false;
    }
    if (base === '*') continue;
    const dash = base.indexOf('-');
    if (dash === -1) {
      const n = Number(base);
      if (!Number.isInteger(n) || n < lo || n > hi) return false;
    } else {
      const a = Number(base.slice(0, dash));
      const b = Number(base.slice(dash + 1));
      if (!Number.isInteger(a) || !Number.isInteger(b) || a < lo || b > hi || a > b) return false;
    }
  }
  return true;
}

/**
 * Turn a 5-field cron string into a human sentence. Throws on a
 * malformed or out-of-range expression. Tuned for the cadences
 * cronFromNatural emits, with a readable fallback for anything else.
 */
export function describeCron(cron) {
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length !== 5 || !parts.every(p => /^[\d*,\-/]+$/.test(p))) {
    throw new Error('invalid cron');
  }
  if (parts.some((p, i) => !fieldInRange(p, FIELD_RANGES[i][0], FIELD_RANGES[i][1]))) {
    throw new Error('cron field out of range');
  }
  const [min, hr, dom, , dow] = parts;
  const minStep = min.match(/^\*\/(\d+)$/);
  const hrStep = hr.match(/^\*\/(\d+)$/);

  let lead;
  let timeBased = false;
  if (min === '*' && hr === '*') {
    lead = 'Every minute';
  } else if (minStep && hr === '*') {
    lead = `Every ${minStep[1]} minutes`;
  } else if (min === '0' && hr === '*') {
    lead = 'Every hour';
  } else if (min === '0' && hrStep) {
    lead = `Every ${hrStep[1]} hours`;
  } else if (/^\d{1,2}$/.test(min) && /^\d{1,2}$/.test(hr)) {
    lead = `At ${pad2(Number(hr))}:${pad2(Number(min))} UTC`;
    timeBased = true;
  } else {
    lead = `Minute ${min}, hour ${hr} (UTC)`;
  }

  const domPhrase = describeDom(dom);
  const dowPhrase = describeDow(dow);
  // A plain daily time reads better with an explicit "every day".
  const everyDay = timeBased && !domPhrase && !dowPhrase ? ' every day' : '';
  return lead + everyDay + domPhrase + dowPhrase;
}
